import { invalidateFormConfigs } from '@/features/config/configApi';
import { adminApi } from '../../api/adminApi';

const LIST_TAG = { type: 'ADMIN_FORM_FIELD_CONFIG', id: 'LIST' };
const BASE_URL = '/form-field-configs';

// The Settings table keeps its own copy of the user's configs; refresh it after a change here
const refreshUserFormConfigs = async (_args, { dispatch, queryFulfilled }) => {
	try {
		await queryFulfilled;
		dispatch(invalidateFormConfigs());
	} catch {
		// Nothing changed
	}
};

/**
 * Admin form field config endpoints. A scope is { scope: 'default' | 'ledger' | 'brokerGroup',
 * scopeId } (scopeId is null for the default).
 * - listFormFieldConfigs({ scope, scopeId, configType }): the configs that scope sees, one per
 *   field, each with its own scope and scopeId.
 * - batchUpdateFormFieldConfigs({ scope, scopeId, changes: [{ id, properties }] }): saves every
 *   change into the scope (a copy of config `id` when the scope has no config for that field
 *   yet), all or nothing.
 * - copyFormFieldConfigs({ docIds, targets: [{ scope, scopeId }] }): copies configs into any
 *   ledgers and broker groups, replacing theirs (no link).
 * - listShareableBrokerGroups({ docIds }): the broker group ids those configs can be shared
 *   into (on their ledger, linked to their groups).
 * - shareFormFieldConfigs({ docIds, brokerGroupIds }): shares broker group configs into those
 *   groups, read-only there and following the originals.
 * - unlinkFormFieldConfigs({ scope, scopeId, docIds }): breaks a broker group's shared configs'
 *   links.
 * - resetFormFieldConfigs({ scope, scopeId, docIds }): deletes those configs of a ledger or
 *   broker group, so their fields follow the next scope in line again.
 */
export const adminFormFieldConfigsApi = adminApi.injectEndpoints({
	endpoints: (builder) => ({
		listFormFieldConfigs: builder.query({
			query: (body) => ({ url: `${BASE_URL}/list`, method: 'POST', body }),
			transformResponse: (response) => response.items,
			providesTags: [LIST_TAG],
		}),
		batchUpdateFormFieldConfigs: builder.mutation({
			query: (body) => ({ url: `${BASE_URL}/batch/update`, method: 'POST', body }),
			invalidatesTags: [LIST_TAG],
			onQueryStarted: refreshUserFormConfigs,
		}),
		copyFormFieldConfigs: builder.mutation({
			query: (body) => ({ url: `${BASE_URL}/copy`, method: 'POST', body }),
			invalidatesTags: [LIST_TAG],
			onQueryStarted: refreshUserFormConfigs,
		}),
		listShareableBrokerGroups: builder.query({
			query: (body) => ({
				url: `${BASE_URL}/share/broker-groups`,
				method: 'POST',
				body,
			}),
			transformResponse: (response) => response.brokerGroupIds,
		}),
		shareFormFieldConfigs: builder.mutation({
			query: (body) => ({ url: `${BASE_URL}/share`, method: 'POST', body }),
			invalidatesTags: [LIST_TAG],
			onQueryStarted: refreshUserFormConfigs,
		}),
		unlinkFormFieldConfigs: builder.mutation({
			query: (body) => ({ url: `${BASE_URL}/unlink`, method: 'POST', body }),
			invalidatesTags: [LIST_TAG],
			onQueryStarted: refreshUserFormConfigs,
		}),
		resetFormFieldConfigs: builder.mutation({
			query: (body) => ({ url: `${BASE_URL}/reset`, method: 'POST', body }),
			invalidatesTags: [LIST_TAG],
			onQueryStarted: refreshUserFormConfigs,
		}),
	}),
});

export const {
	useListFormFieldConfigsQuery,
	useBatchUpdateFormFieldConfigsMutation,
	useCopyFormFieldConfigsMutation,
	useListShareableBrokerGroupsQuery,
	useShareFormFieldConfigsMutation,
	useUnlinkFormFieldConfigsMutation,
	useResetFormFieldConfigsMutation,
} = adminFormFieldConfigsApi;
